// src/data/typeDefinitions.js
// Maps the 6 worldview clusters to short, shareable type names + viral payload.
// Used by free results (TypeCard, MoralFingerprint), slide reveal, and deep-dive.

export const TYPE_DEFINITIONS = {
  'Equity Champions': {
    typeName: 'The Compass',
    tagline: 'Guided by justice, you point the way forward.',
    shortDescription:
      'You see unfairness like others see color — it’s everywhere, obvious, and demands a response. Your instinct is to fix systems, not just symptoms. You’re the person who asks "but is it fair?" when everyone else has moved on.',
    blindSpot:
      'You sometimes forget that people can disagree with your methods while sharing your values. Not everyone who moves slower than you is the enemy.',
    oneLiner: 'You’d rather be right and alone than wrong and comfortable.',
    color: '#C45B4F',
    bgColor: '#FBF3F2',
    lightColor: '#DB9C97',
    // Viral payload
    rarityPercent: 14,
    controversialOpinion:
      'Most "neutrality" is just cowardice with better marketing.',
    enemiesSay:
      'You’re exhausting. Everything is a hill to die on. You can’t enjoy a brunch without auditing the menu’s ethics.',
    datingRedFlag: 'First fight: how they tip. Second fight: still the tip.',
    superpower: 'Spotting the unfair deal everyone else has agreed to ignore.',
    popCultureMatches: ['Hermione Granger', 'Captain America', 'Lisa Simpson'],
    compatibleWith: 'The Torch',
    clashesWith: 'The Anchor',
    iconKey: 'Compass',
  },
  'Progressive Catalysts': {
    typeName: 'The Torch',
    tagline: 'You illuminate paths others can’t yet see.',
    shortDescription:
      'You’re wired to question everything and build something better. Where others see "how things are," you see "how things could be." Your restlessness isn’t anxiety — it’s vision.',
    blindSpot:
      'Your certainty about what needs to change can make you dismissive of what already works. Sometimes the old way earned its place.',
    oneLiner: 'You’d rather build something new than fix something old.',
    color: '#D4A843',
    bgColor: '#FBF6EB',
    lightColor: '#EDDBAA',
    rarityPercent: 11,
    controversialOpinion:
      'Most traditions are just peer pressure from dead people.',
    enemiesSay:
      'You’re allergic to "we’ve always done it this way." You’d rebrand a funeral.',
    datingRedFlag:
      'You’ll request a quarterly review of the relationship. With KPIs.',
    superpower: 'Imagining a future the rest of us haven’t earned yet.',
    popCultureMatches: ['Tony Stark', 'Doc Brown', 'Lin-Manuel Miranda'],
    compatibleWith: 'The Architect',
    clashesWith: 'The Anchor',
    iconKey: 'Flame',
  },
  'Expressive Innovators': {
    typeName: 'The Mirror',
    tagline: 'You reflect truths others aren’t ready to face.',
    shortDescription:
      'You process the world through feeling first. Your emotional intelligence is your superpower and your vulnerability. You understand people at a depth that makes some uncomfortable.',
    blindSpot:
      'Your need for authenticity can become its own rigidity — insisting things must feel right before they can be right.',
    oneLiner:
      'You feel everything twice — once for yourself, once for everyone else.',
    color: '#6B8F71',
    bgColor: '#F2F6F2',
    lightColor: '#C1D5C3',
    rarityPercent: 17,
    controversialOpinion:
      'Your gut figured it out three weeks ago. You’re just catching up.',
    enemiesSay:
      'You make every conversation a therapy session. You can sense vibes from three rooms away and you will not let it go.',
    datingRedFlag:
      'You’ll know the relationship is over six weeks before they do.',
    superpower: 'Reading the room so accurately it’s almost telepathic.',
    popCultureMatches: ['Fleabag', 'Phoebe Buffay', 'Lana Del Rey'],
    compatibleWith: 'The Bridge',
    clashesWith: 'The Architect',
    iconKey: 'Sparkles',
  },
  'Balanced Nonconformists': {
    typeName: 'The Bridge',
    tagline: 'You connect worlds that refuse to talk to each other.',
    shortDescription:
      'You hold contradictions without flinching. Where others pick a side, you see the full picture. Your refusal to be categorized isn’t indecision — it’s a higher form of clarity.',
    blindSpot:
      'Your ability to see all sides can paralyze you when decisive action is needed. Sometimes you have to pick a side.',
    oneLiner:
      'You’re the person both sides of an argument trust — and neither fully understands.',
    color: '#7C8186',
    bgColor: '#F5F5F4',
    lightColor: '#B8BCBF',
    rarityPercent: 19,
    controversialOpinion:
      'Most arguments are two people agreeing in different vocabularies.',
    enemiesSay:
      'Just pick a side. Please. We’ve been at this restaurant for forty minutes.',
    datingRedFlag:
      'Three years in, still hasn’t picked the restaurant. Will never pick the restaurant.',
    superpower: 'Translating between people who can’t hear each other.',
    popCultureMatches: ['Ted Lasso', 'Mr. Rogers', 'Obi-Wan Kenobi'],
    compatibleWith: 'The Mirror',
    clashesWith: 'The Compass',
    iconKey: 'Network',
  },
  'Pragmatic Visionaries': {
    typeName: 'The Architect',
    tagline: 'You build what works, not what sounds good.',
    shortDescription:
      'You combine big-picture thinking with ground-level execution. Dreams without plans are just wishes to you. You’re the person who turns "wouldn’t it be nice if..." into a spreadsheet with deadlines.',
    blindSpot:
      'Your focus on results can make you blind to the human cost of efficient solutions. Not everything worth doing can be measured.',
    oneLiner: 'You have a 5-year plan for your 5-year plan.',
    color: '#3A7D7E',
    bgColor: '#EDF5F5',
    lightColor: '#8BB8B9',
    rarityPercent: 21,
    controversialOpinion:
      'Feelings are data. Useful data. But still data — not the answer.',
    enemiesSay:
      'You’d run an Excel sheet on your own funeral. You’ve never made a "vibe-based" decision in your life.',
    datingRedFlag:
      'You’ll google "how to be more spontaneous" and then schedule it.',
    superpower:
      'Turning chaos into a workable plan before anyone else has finished panicking.',
    popCultureMatches: ['Olivia Pope', 'Ron Swanson', 'Monica Geller'],
    compatibleWith: 'The Torch',
    clashesWith: 'The Mirror',
    iconKey: 'Ruler',
  },
  'Traditional Guardians': {
    typeName: 'The Anchor',
    tagline: 'You hold the line when everything else is shifting.',
    shortDescription:
      'You understand that stability isn’t the enemy of progress — it’s the foundation for it. While others chase the new, you protect what matters. You’re the reason institutions survive long enough to evolve.',
    blindSpot:
      'Your loyalty to what worked before can make you resist changes that are genuinely needed. Not all disruption is destruction.',
    oneLiner: 'You believe the old ways earned their place for a reason.',
    color: '#4A5568',
    bgColor: '#F0F0EE',
    lightColor: '#9AA5B0',
    rarityPercent: 18,
    controversialOpinion:
      'Most "progress" is just stuff that hasn’t aged into a problem yet.',
    enemiesSay:
      'You’re the reason the family group chat still uses Facebook. You distrust anything that didn’t exist when you were 22.',
    datingRedFlag:
      'You’ll compare them to your ex. Your high school ex. Who you broke up with at 17.',
    superpower: 'Seeing through hype to what will still matter in ten years.',
    popCultureMatches: ['Uncle Iroh', 'Mufasa', 'Captain Holt'],
    compatibleWith: 'The Bridge',
    clashesWith: 'The Torch',
    iconKey: 'Anchor',
  },
};

// Quick lookup: cluster name -> type name
export const CLUSTER_TO_TYPE = Object.fromEntries(
  Object.entries(TYPE_DEFINITIONS).map(([cluster, def]) => [
    cluster,
    def.typeName,
  ])
);

// Quick lookup: type name -> cluster name
export const TYPE_TO_CLUSTER = Object.fromEntries(
  Object.entries(TYPE_DEFINITIONS).map(([cluster, def]) => [
    def.typeName,
    cluster,
  ])
);

// Get type definition by cluster name
export function getTypeByCluster(clusterName) {
  return (
    TYPE_DEFINITIONS[clusterName] || TYPE_DEFINITIONS['Pragmatic Visionaries']
  );
}

// Get type definition by typeName (e.g. "The Compass")
export function getTypeByName(typeName) {
  const cluster = TYPE_TO_CLUSTER[typeName];
  return cluster ? TYPE_DEFINITIONS[cluster] : null;
}

export default TYPE_DEFINITIONS;
